import { RTU } from '../types/RTU';
import { RTUData } from '../types/RtuData';
import { RtuCanonicalStatus, RtuDisplayStatus, RtuUiStatus } from '../types/RtuStatus';

export const STATUS_COLORS = {
  online: '#28a745',
  unstable: '#f0ad4e',
  offline: '#6c757d',
  rebooting: '#0d6efd',
  warning: '#f0ad4e',
  maintenance: '#0d6efd',
  unknown: '#9aa4ae',
} as const;

export type OperationalStatus = keyof typeof STATUS_COLORS;

const normalizeStatusValue = (value: unknown): string => String(value ?? '').trim().toLowerCase();

export const normalizeCanonicalStatus = (value: unknown): RtuCanonicalStatus => {
  const normalized = String(value ?? '').trim().toUpperCase();

  switch (normalized) {
    case 'ONLINE':
      return 'ONLINE';
    case 'UNSTABLE':
      return 'UNSTABLE';
    case 'OFFLINE':
      return 'OFFLINE';
    case 'UNKNOWN':
      return 'UNKNOWN';
    default:
      return 'UNKNOWN';
  }
};

export const toUiStatus = (value: unknown): RtuUiStatus => {
  const canonical = normalizeCanonicalStatus(value);
  switch (canonical) {
    case 'ONLINE':
      return 'online';
    case 'UNSTABLE':
      return 'unstable';
    case 'OFFLINE':
      return 'offline';
    default:
      return 'unknown';
  }
};

export const toCompatibilityOnlineStatus = (value: unknown): boolean | null => {
  const canonical = normalizeCanonicalStatus(value);

  if (canonical === 'ONLINE') {
    return true;
  }

  if (canonical === 'OFFLINE') {
    return false;
  }

  return null;
};

const inferCanonicalFromLegacy = (status: unknown, onlineStatus: unknown): RtuCanonicalStatus => {
  if (onlineStatus === true) {
    return 'ONLINE';
  }

  if (onlineStatus === false) {
    return 'OFFLINE';
  }

  const normalizedStatus = normalizeOperationalStatus(status);
  if (normalizedStatus === 'online' || normalizedStatus === 'rebooting') {
    return 'ONLINE';
  }
  if (normalizedStatus === 'offline') {
    return 'OFFLINE';
  }

  return 'UNKNOWN';
};

export const getCanonicalStatusFromRecord = (record: {
  canonicalStatus?: unknown;
  status?: unknown;
  onlineStatus?: unknown;
}): RtuCanonicalStatus => {
  if (record.canonicalStatus !== undefined && record.canonicalStatus !== null && record.canonicalStatus !== '') {
    return normalizeCanonicalStatus(record.canonicalStatus);
  }

  return inferCanonicalFromLegacy(record.status, record.onlineStatus);
};

export const getDisplayStatusFromRecord = (record: {
  canonicalStatus?: unknown;
  status?: unknown;
  onlineStatus?: unknown;
  systemRestart?: unknown;
}): RtuDisplayStatus => {
  if (record.systemRestart === true) {
    return 'rebooting';
  }

  return toUiStatus(getCanonicalStatusFromRecord(record));
};

export const withCanonicalStatusCompatibility = <T extends RTU>(rtu: T): T => {
  const canonicalStatus = getCanonicalStatusFromRecord(rtu);

  return {
    ...rtu,
    canonicalStatus,
    status: getDisplayStatusFromRecord({ ...rtu, canonicalStatus }),
    onlineStatus: toCompatibilityOnlineStatus(canonicalStatus),
  };
};

export const withCanonicalStatusCompatibilityForDetail = <T extends RTUData>(rtuData: T): T => {
  const canonicalStatus = getCanonicalStatusFromRecord(rtuData);

  return {
    ...rtuData,
    canonicalStatus,
    onlineStatus: toCompatibilityOnlineStatus(canonicalStatus),
  };
};

export const normalizeOperationalStatus = (value: unknown): OperationalStatus => {
  if (value === true) {
    return 'online';
  }

  if (value === false) {
    return 'offline';
  }

  const normalized = normalizeStatusValue(value);

  switch (normalized) {
    case 'online':
    case 'running':
      return 'online';
    case 'offline':
    case 'down':
      return 'offline';
    case 'unstable':
    case 'degraded':
    case 'partial':
      return 'unstable';
    case 'rebooting':
    case 'restarting':
      return 'rebooting';
    case 'warning':
      return 'warning';
    case 'maintenance':
      return 'maintenance';
    default:
      return 'unknown';
  }
};

export const getStatusColor = (value: unknown): string =>
  STATUS_COLORS[normalizeOperationalStatus(value)];

export const getStatusLabelKey = (value: unknown): string => {
  const status = normalizeOperationalStatus(value);
  if (status === 'offline') {
    return 'status.offlineNotReachable';
  }
  return `status.${status}`;
};

/** Canonical Assets / Grouped Assets status filter values (Bug 3145 / 3152). */
export const ASSET_STATUS_FILTER_VALUES = ['online', 'offline', 'unstable', 'rebooting'] as const;
export type AssetStatusFilterValue = (typeof ASSET_STATUS_FILTER_VALUES)[number];

export const getAssetStatusFilterLabelKey = (value: AssetStatusFilterValue): string =>
  getStatusLabelKey(value);

export const buildAssetStatusFilterOptions = (
  translate: (key: string, options?: Record<string, unknown>) => string
): Array<{ label: string; value: AssetStatusFilterValue }> =>
  ASSET_STATUS_FILTER_VALUES.map((value) => ({
    value,
    label: translate(getAssetStatusFilterLabelKey(value), {
      defaultValue:
        value === 'offline'
          ? 'Offline / Not Reachable'
          : `${value.charAt(0).toUpperCase()}${value.slice(1)}`,
    }),
  }));

interface CanonicalStatusFields {
  canonicalStatus?: unknown;
  status?: unknown;
  onlineStatus?: unknown;
  statusObservedAtUtc?: string | null;
  statusSource?: unknown;
  statusReason?: unknown;
  lastProbeAtUtc?: string | null;
  lastFileReceivedAtUtc?: string | null;
}

const parseTimestamp = (value: string | null | undefined): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

/** Later of two ISO timestamps, treating a missing side as "no evidence" rather than "oldest". */
const laterIso = (a: string | null | undefined, b: string | null | undefined): string | null | undefined => {
  const at = parseTimestamp(a);
  const bt = parseTimestamp(b);
  if (at === null) return b ?? a;
  if (bt === null) return a ?? b;
  return bt > at ? b : a;
};

/**
 * Bug 3154: the single point where an incoming REST or WebSocket RTU payload
 * is reconciled against whatever canonical status is already held in Redux.
 * Canonical status/source/reason move together as one unit, gated by
 * `statusObservedAtUtc` - a payload is only allowed to replace them when its
 * observation is strictly newer (or nothing canonical is known yet).
 * `lastProbeAtUtc`/`lastFileReceivedAtUtc` are independent freshness
 * evidence and always advance to whichever side is later, matching the
 * backend's own evidence-retention contract. A payload that carries no
 * observation timestamp and no canonical status at all (e.g. a partial
 * non-status realtime update) never erases a previously known status.
 * All other (non-status) fields on `incoming` are left untouched - this
 * function only ever overrides the fields listed above.
 */
export const mergeCanonicalStatusFields = <T extends CanonicalStatusFields>(
  previous: T | null | undefined,
  incoming: T
): T => {
  if (!previous) {
    return incoming;
  }

  const incomingObservedAt = parseTimestamp(incoming.statusObservedAtUtc);
  const previousObservedAt = parseTimestamp(previous.statusObservedAtUtc);
  const incomingHasNoStatusEvidence = incomingObservedAt === null && (incoming.canonicalStatus === undefined || incoming.canonicalStatus === null || incoming.canonicalStatus === '');

  const keepPreviousCanonical = incomingHasNoStatusEvidence
    || (previousObservedAt !== null && incomingObservedAt !== null && incomingObservedAt <= previousObservedAt)
    || (previousObservedAt !== null && incomingObservedAt === null);

  const canonicalOverrides = keepPreviousCanonical
    ? {
        canonicalStatus: previous.canonicalStatus,
        status: previous.status,
        onlineStatus: previous.onlineStatus,
        statusObservedAtUtc: previous.statusObservedAtUtc,
        statusSource: previous.statusSource,
        statusReason: previous.statusReason,
      }
    : {};

  return {
    ...incoming,
    ...canonicalOverrides,
    lastProbeAtUtc: laterIso(previous.lastProbeAtUtc, incoming.lastProbeAtUtc),
    lastFileReceivedAtUtc: laterIso(previous.lastFileReceivedAtUtc, incoming.lastFileReceivedAtUtc),
  };
};
